'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Entry {
  readonly label: string;
  readonly hint?: string;
  readonly href?: string;
  readonly run?: () => void;
  readonly section: string;
}

const COMMANDS: readonly Entry[] = [
  { section: 'Navigate', label: 'Home', href: '/' },
  { section: 'Navigate', label: 'Live theater', href: '/live' },
  { section: 'Navigate', label: 'Conversations', href: '/conversations' },
  { section: 'Navigate', label: 'Knowledge', href: '/knowledge' },
  { section: 'Navigate', label: 'Agent', href: '/agent' },
  { section: 'Navigate', label: 'Billing', href: '/billing' },
  { section: 'Navigate', label: 'Team', href: '/team' },
  { section: 'Navigate', label: 'Settings', href: '/settings' },
  { section: 'Do', label: 'Add knowledge source', href: '/knowledge?add=1' },
  { section: 'Do', label: 'Invite teammate', href: '/team?invite=1' },
  { section: 'Do', label: 'View this month\u2019s invoice', href: '/billing?tab=invoices' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query) return COMMANDS;
    const q = query.toLowerCase();
    return COMMANDS.filter(
      (c) => c.label.toLowerCase().includes(q) || c.section.toLowerCase().includes(q),
    );
  }, [query]);

  const run = useCallback(
    (entry: Entry) => {
      setOpen(false);
      if (entry.href) router.push(entry.href);
      else entry.run?.();
    },
    [router],
  );

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,20,25,.32)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '10vh',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          background: 'var(--perch-color-paper-raised)',
          borderRadius: 16,
          boxShadow: 'var(--perch-shadow-lift)',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type to search…"
          aria-label="Command"
          style={{
            width: '100%',
            padding: '14px 18px',
            font: 'inherit',
            border: 0,
            outline: 0,
            fontSize: 16,
            borderBottom: '1px solid var(--perch-color-stone-soft)',
            background: 'transparent',
          }}
        />
        <ul
          role="listbox"
          style={{ listStyle: 'none', margin: 0, padding: 8, maxHeight: 380, overflowY: 'auto' }}
        >
          {filtered.map((e) => (
            <li key={`${e.section}-${e.label}`}>
              <button
                type="button"
                onClick={() => run(e)}
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  border: 0,
                  background: 'transparent',
                  borderRadius: 8,
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                }}
                onMouseEnter={(ev) =>
                  (ev.currentTarget.style.background = 'var(--perch-color-paper-sunken)')
                }
                onMouseLeave={(ev) => (ev.currentTarget.style.background = 'transparent')}
              >
                <span>{e.label}</span>
                <span style={{ color: 'var(--perch-color-ink-whisper)', fontSize: 12 }}>
                  {e.section}
                </span>
              </button>
            </li>
          ))}
          {filtered.length === 0 ? (
            <li style={{ padding: 12, color: 'var(--perch-color-ink-whisper)' }}>No matches.</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}
